import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Redirect, Route, RouteProps, useLocation } from 'react-router'
import queryString from 'query-string'
import { isCurrentUserLoadingInProgress, selectCurrentUser } from 'store/users/users.selectors'
import { LoadingPlaceholder } from 'components/atoms/LoadingPlaceholder'
import { intendedOrderTypeStorageKey, returnAfterAuthStorageKey } from 'routes/url-constants'

export interface AuthRouteRouteProps extends RouteProps {
  redirectAuthorizedTo: string
}

export const AuthRoute: React.FC<AuthRouteRouteProps> = ({ redirectAuthorizedTo, ...routeProps }) => {
  const currentUser = useSelector(selectCurrentUser)
  const isLoadingInProgress = useSelector(isCurrentUserLoadingInProgress)
  const location = useLocation()
  const query = useMemo(() => queryString.parse(location.search), [location])

  if (isLoadingInProgress) {
    return <LoadingPlaceholder />
  }

  const isSafePathOrSameOrigin = (raw: string): string | null => {
    if (!raw) return null
    if (raw.startsWith('/')) {
      if (raw.startsWith('//')) return null
      return raw
    }

    try {
      const u = new URL(raw)
      if (u.origin === window.location.origin) {
        return `${u.pathname}${u.search}${u.hash}`
      }
    } catch {
      return null
    }

    return null
  }

  const fromQuery = typeof query.backUrl === 'string' ? isSafePathOrSameOrigin(query.backUrl) : null
  const fromSession = (() => {
    try {
      return isSafePathOrSameOrigin(sessionStorage.getItem(returnAfterAuthStorageKey) || '')
    } catch {
      return null
    }
  })()

  const backUrl = fromQuery || fromSession

  if (currentUser && backUrl) {
    try {
      sessionStorage.removeItem(returnAfterAuthStorageKey)
      // If destination already contains orderType, drop the fallback key
      if (backUrl.includes('orderType=')) {
        sessionStorage.removeItem(intendedOrderTypeStorageKey)
      }
    } catch {
      // ignore
    }
    window.location.href = backUrl
    return <LoadingPlaceholder />
  }

  if (currentUser) {
    return <Redirect to={redirectAuthorizedTo} />
  }

  return <Route {...routeProps} />
}
